import { useState } from 'react'
import { Box, Card, CardContent, Stack, Typography, Alert, Link as MuiLink, TextField, MenuItem } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { getDashboardSummary, listRapoarteWorkPoints, type AlertSeverity } from '../api/rapoarte'

const MUI_SEVERITY: Record<AlertSeverity, 'info' | 'warning' | 'error'> = {
  Info: 'info', Warning: 'warning', Critical: 'error',
}

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [workPointId, setWorkPointId] = useState('')

  const { data: workPoints } = useQuery({ queryKey: ['rapoarte', 'puncte-lucru'], queryFn: listRapoarteWorkPoints })
  const { data } = useQuery({
    queryKey: ['rapoarte', 'dashboard', workPointId || null],
    queryFn: () => getDashboardSummary(workPointId || undefined),
    refetchInterval: 60_000,
  })

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="overline" color="primary">Tablou de bord</Typography>
          <Typography variant="h2">Bun venit, {user?.fullName.split(' ')[0]}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {user?.workPointName} · {user?.roles.join(', ')}
          </Typography>
        </Box>
        <TextField select label="Punct de lucru" size="small" sx={{ width: 220 }}
          value={workPointId} onChange={(e) => setWorkPointId(e.target.value)}>
          <MenuItem value="">Toate</MenuItem>
          {workPoints?.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
        </TextField>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
        <StatCard label="Valoare stoc (Liber)" value={data ? data.stockValue.toFixed(2).replace('.', ',') : '—'}
          detail="Sold Liber × cost standard" />
        <StatCard label="Comenzi noi" value={String(data?.newOrdersLast7Days ?? '—')} detail="Ultimele 7 zile" />
        <StatCard label="OP-uri în execuție" value={String(data?.productionOrdersInExecution ?? '—')} detail="Producție, status curent" />
        <StatCard label="Alerte active" value={String(data?.totalActiveAlerts ?? '—')}
          detail="Vezi toate alertele"
          tone={data && data.totalActiveAlerts > 0 ? 'warning' : undefined} />
      </Box>

      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">Alerte critice</Typography>
        {data && data.topAlerts.length === 0 && (
          <Alert severity="success" variant="outlined">Niciun eveniment activ care necesită atenție.</Alert>
        )}
        {data?.topAlerts.map((a, i) => (
          <Alert key={i} severity={MUI_SEVERITY[a.severity]} variant="outlined"
            onClick={() => navigate(a.linkPath)} sx={{ cursor: 'pointer' }}>
            <strong>{a.title}</strong> — {a.detail}
          </Alert>
        ))}
        {data && data.totalActiveAlerts > data.topAlerts.length && (
          <MuiLink component="button" variant="body2" onClick={() => navigate('/alerte')} sx={{ textAlign: 'left' }}>
            Vezi toate alertele ({data.totalActiveAlerts}) →
          </MuiLink>
        )}
      </Stack>
    </Stack>
  )
}

function StatCard({ label, value, detail, tone }: {
  label: string; value: string; detail: string; tone?: 'success' | 'warning'
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h3" sx={{ fontFamily: '"IBM Plex Mono", monospace', mt: 0.5, color: tone ? `${tone}.main` : undefined }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </CardContent>
    </Card>
  )
}
